/**
 * AlertsList — sorted-by-severity alerts with color-coded severity
 * pips. Clicking an alert that references a GI jumps to its detail.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { AlertSeverity, CommandAlert } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatRelativeTime } from '@/utils/format';
import { cn } from '@/utils/cn';

const SEVERITY_ORDER: Record<AlertSeverity, number> = {
  critical: 0,
  warn: 1,
  info: 2,
};

export function AlertsList({ alerts }: { alerts: CommandAlert[] }) {
  const { t } = useTranslation('command');
  const locale = useLocaleStore((s) => s.locale);

  if (alerts.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-surface p-4 text-sm text-ink-muted">
        {t('alerts.empty')}
      </p>
    );
  }

  const sorted = alerts.slice().sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
      {sorted.map((alert, i) => (
        <li key={`${alert.kind}-${i}`} className="flex items-start gap-3 px-4 py-3">
          <Pip severity={alert.severity} />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <AlertBody alert={alert} />
            <span className="text-[11px] text-ink-subtle">
              {formatRelativeTime(alert.at.iso, locale)}
            </span>
            {alert.kind === 'gi_failed' ? (
              <span className="text-[11px] text-ink-muted">
                {t('alerts.reason_failed', { reason: alert.reason })}
              </span>
            ) : null}
          </div>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
              alert.severity === 'critical' && 'bg-danger/10 text-danger',
              alert.severity === 'warn' && 'bg-warn/10 text-warn',
              alert.severity === 'info' && 'bg-spark-soft text-spark',
            )}
          >
            {t(`alerts.severity.${alert.severity}`)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function AlertBody({ alert }: { alert: CommandAlert }) {
  const { t } = useTranslation('command');
  switch (alert.kind) {
    case 'gi_failed':
      return (
        <Link to={`/gi/${alert.root_id}`} className="truncate text-sm text-ink hover:underline">
          {t('alerts.kind.gi_failed', { name: alert.name })}
        </Link>
      );
    case 'witness_down':
      return (
        <span className="truncate text-sm text-ink">
          {t('alerts.kind.witness_down', { witness: alert.witness })}
        </span>
      );
    case 'new_fork':
      return (
        <Link to={`/gi/${alert.root_id}`} className="truncate text-sm text-ink hover:underline">
          {t('alerts.kind.new_fork', { name: alert.name, by: alert.by })}
        </Link>
      );
    case 'revenue_spike':
      return (
        <Link to={`/gi/${alert.root_id}`} className="truncate text-sm text-ink hover:underline">
          {t('alerts.kind.revenue_spike', { name: alert.name, amount: alert.amount_ils })}
        </Link>
      );
  }
}

function Pip({ severity }: { severity: AlertSeverity }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'mt-1 inline-block h-2 w-2 shrink-0 rounded-full',
        severity === 'critical' && 'bg-danger',
        severity === 'warn' && 'bg-warn',
        severity === 'info' && 'bg-spark',
      )}
    />
  );
}
